/**
 * Slide indicators — progress bars for product card galleries and PDP slideshows.
 * Cards: left-aligned inside image. PDP: centered, replaces the "1/7" counter.
 */
(function () {
  if (window.__cardGalleryIndicatorsInit) return;
  window.__cardGalleryIndicatorsInit = true;

  /* ── inject styles ───────────────────────────────────────────────── */
  const css = `
    .cg-indicators {
      position: absolute;
      bottom: max(var(--padding-sm, 8px), calc((var(--border-radius, 0px) + var(--padding-sm, 8px)) * (1 - cos(45deg))));
      left: max(var(--padding-sm, 8px), calc((var(--border-radius, 0px) + var(--padding-sm, 8px)) * (1 - cos(45deg))));
      display: flex;
      gap: 2px;
      z-index: 2;
      pointer-events: none;
      align-items: center;
    }
    .cg-indicators__bar {
      height: 4px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.4);
      transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  background 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      width: 8px;
    }
    .cg-indicators__bar--active {
      background: rgba(255, 255, 255, 0.95);
      width: 16px;
    }
    .card-gallery { position: relative; }

    /* PDP: replace the counter with centered bars */
    .cg-indicators--pdp {
      position: static;
      display: flex;
      gap: 3px;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .cg-indicators--pdp .cg-indicators__bar {
      width: 12px;
    }
    .cg-indicators--pdp .cg-indicators__bar--active {
      width: 28px;
    }
    .slideshow-controls__counter:has(.cg-indicators--pdp) {
      display: flex;
      justify-content: center;
      background-color: transparent;
      backdrop-filter: none;
      padding: 0;
      border-radius: 0;
    }
  `;
  const styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  const INDICATOR_CLASS = 'cg-indicators';
  const BAR_CLASS = 'cg-indicators__bar';
  const BAR_ACTIVE_CLASS = 'cg-indicators__bar--active';

  /* ── helpers ──────────────────────────────────────────────────────── */

  function getVisibleSlides(slideshow) {
    // Exclude hidden variant-image slides
    const all = slideshow.querySelectorAll('slideshow-slides > *');
    return Array.from(all).filter(
      (s) => s.getAttribute('aria-hidden') !== 'true' && !s.hidden
    );
  }

  function createIndicator(count) {
    const wrap = document.createElement('div');
    wrap.className = INDICATOR_CLASS;
    wrap.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < count; i++) {
      const bar = document.createElement('span');
      bar.className = BAR_CLASS;
      if (i === 0) bar.classList.add(BAR_ACTIVE_CLASS);
      wrap.appendChild(bar);
    }
    return wrap;
  }

  function updateIndicator(indicator, index) {
    const bars = indicator.children;
    for (let i = 0; i < bars.length; i++) {
      bars[i].classList.toggle(BAR_ACTIVE_CLASS, i === index);
    }
  }

  /* ── per-card setup ──────────────────────────────────────────────── */

  function initCard(gallery) {
    if (gallery.dataset.cgIndicatorReady === 'true') return;
    gallery.dataset.cgIndicatorReady = 'true';

    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;

    // Wait for custom element to be defined and connected
    function setup() {
      const slides = slideshow.slides || getVisibleSlides(slideshow);
      if (!slides || slides.length <= 1) return;

      const indicator = createIndicator(slides.length);
      // Insert inside the card-gallery, over the image
      gallery.appendChild(indicator);

      // Use an IntersectionObserver on the scroller's children
      const scroller = slideshow.querySelector('slideshow-slides');
      if (!scroller) return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const idx = slides.indexOf(entry.target);
              if (idx !== -1) updateIndicator(indicator, idx);
            }
          }
        },
        { root: scroller, threshold: 0.5 }
      );

      slides.forEach((slide) => observer.observe(slide));
    }

    // The slideshow may not be fully initialised yet
    if (slideshow.slides && slideshow.slides.length) {
      setup();
    } else {
      // Retry after a short delay (custom element upgrade)
      requestAnimationFrame(() => setTimeout(setup, 80));
    }
  }

  /* ── PDP slideshow setup ───────────────────────────────────────── */

  function initPdpSlideshow(counter) {
    if (counter.dataset.cgIndicatorReady === 'true') return;
    counter.dataset.cgIndicatorReady = 'true';

    const slideshow = counter.closest('slideshow-component');
    if (!slideshow) return;

    function setup() {
      const slides = slideshow.slides || getVisibleSlides(slideshow);
      if (!slides || slides.length <= 1) return;

      const indicator = createIndicator(slides.length);
      indicator.classList.add('cg-indicators--pdp');

      // Replace counter content with bars
      counter.replaceChildren(indicator);

      const scroller = slideshow.querySelector('slideshow-slides');
      if (!scroller) return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const idx = slides.indexOf(entry.target);
              if (idx !== -1) updateIndicator(indicator, idx);
            }
          }
        },
        { root: scroller, threshold: 0.5 }
      );

      slides.forEach((slide) => observer.observe(slide));
    }

    if (slideshow.slides && slideshow.slides.length) {
      setup();
    } else {
      requestAnimationFrame(() => setTimeout(setup, 80));
    }
  }

  /* ── scan & observe ──────────────────────────────────────────────── */

  function scan() {
    document.querySelectorAll('.card-gallery').forEach(initCard);
    document.querySelectorAll('.slideshow-controls__counter').forEach((c) => {
      if (!c.closest('.card-gallery')) initPdpSlideshow(c);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Handle dynamically added cards (AJAX pagination, section rendering, etc.)
  if (typeof MutationObserver !== 'undefined') {
    let pending = false;
    new MutationObserver(() => {
      if (!pending) {
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          scan();
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();